import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState, EmptyState } from "@/components/shared";
import { usePortalData, useSavedJobs, filterJobs, appStatus } from "@/components/candidate_portal/portal";
import { JobsToolbar } from "@/components/candidate_portal/JobsToolbar";
import { JobList } from "@/components/candidate_portal/JobList";
import { JobPane } from "@/components/candidate_portal/JobPane";
import { ApplyDialog } from "@/components/candidate_portal/ApplyDialog";
import { cn } from "@/lib/utils";

const INITIAL = { q: "", loc: "", remote: false, savedOnly: false, sort: "fit" };

function JobsSkeleton() {
  return (
    <div className="grid lg:grid-cols-[400px_minmax(0,1fr)] gap-8 px-4 sm:px-6 py-6" role="status" aria-busy="true" data-testid="opportunities-skeleton">
      <div className="space-y-2.5">
        <div className="flex items-center gap-2 pb-1 text-sm text-muted-foreground"><Sparkles className="h-4 w-4 text-primary animate-pulse" /> Matching you to open roles…</div>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[92px] rounded-[14px]" />)}
      </div>
      <div className="hidden lg:block space-y-5"><Skeleton className="h-14 w-2/3" /><Skeleton className="h-40 rounded-[14px]" /><Skeleton className="h-24 rounded-[14px]" /></div>
    </div>
  );
}

export default function CandidateJobs() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const d = usePortalData();
  const [f, setF] = useState(INITIAL);
  const [saved, toggleSaved] = useSavedJobs(user?.id);
  const [applyFor, setApplyFor] = useState(null);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const picked = params.get("job");
  const pick = (id) => setParams({ job: id });

  const body = (() => {
    if (d.opp === undefined) return <JobsSkeleton />;
    if (d.opp === null) return <div className="p-6"><ErrorState message="We couldn't load roles right now." onRetry={d.reload} /></div>;
    const opps = d.opp.opportunities || [];
    if (!opps.length) return <div className="p-6"><EmptyState icon={Compass} title="No open roles right now" description="Check back soon — new roles are posted regularly." /></div>;
    const ready = d.opp.profile_ready;
    const items = filterJobs(opps, { ...f, saved });
    const appsByJob = Object.fromEntries((d.apps || []).map((a) => [a.job_id, a]));
    const active = opps.find((o) => o.job.id === picked) || items[0];
    const app = active && appsByJob[active.job.id];
    const similar = opps.filter((o) => !o.applied && o.fit != null && o.job.id !== applyFor?.job.id).slice(0, 2);
    return (
      <div className="flex-1 min-h-0 lg:grid lg:grid-cols-[400px_minmax(0,1fr)]">
        <div className={cn("lg:border-r border-border lg:overflow-y-auto px-4 sm:px-6 py-5", picked && "hidden lg:block")}>
          {items.length ? <JobList items={items} activeId={active?.job.id} onPick={pick} appsByJob={appsByJob} interviews={d.interviews} ready={ready} strong={items.filter((o) => o.fit >= 70).length} />
            : <div className="py-12 text-center space-y-3" data-testid="jobs-no-results"><p className="text-sm text-muted-foreground">No roles match these filters.</p><button onClick={() => setF(INITIAL)} className="text-sm text-primary hover:underline" data-testid="jobs-clear-filters">Clear filters</button></div>}
        </div>
        <div className={cn("lg:overflow-y-auto px-4 sm:px-6 lg:px-10 py-6", !picked && "hidden lg:block")}>
          {active && <JobPane o={active} app={app} status={app && appStatus(app, d.interviews)} ready={ready} saved={saved.includes(active.job.id)} onSave={toggleSaved} onApply={() => setApplyFor(active)} onBack={() => setParams({})} />}
        </div>
        <ApplyDialog o={applyFor} profile={d.profile} similar={similar} onClose={() => setApplyFor(null)} onApplied={d.reload} onPick={pick} />
      </div>
    );
  })();

  return (
    <div className="lg:h-[calc(100dvh-60px)] flex flex-col max-w-[1440px] mx-auto" data-testid="candidate-jobs">
      <div className={cn("shrink-0 px-4 sm:px-6 py-5 border-b border-border", picked && "hidden lg:block")}>
        <h1 className="sr-only">Jobs</h1>
        <JobsToolbar f={f} set={set} savedCount={saved.length} auto={d.auto} />
      </div>
      {body}
    </div>
  );
}
